import React, { useState } from 'react';
import { Typography, Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useFormField } from './FormFieldContext';
import HelpTextSideDrawer from './HelpTextSideDrawer';

export const FormFieldLabel: React.FC = () => {
  const {
    fieldMeta,
    field,
    isRequired,
    fieldPresentation,
    isDisabled,
    showRemaining,
    fieldSourceValue,
    isReadOnly,
  } = useFormField();

  const [isHelpDrawerOpen, setIsHelpDrawerOpen] = useState(false);

  const hasHelpText =
    fieldMeta.HelpText !== undefined && fieldMeta.HelpText !== null;

  const renderRemainingLeft =
    typeof showRemaining === 'number' &&
    typeof fieldMeta.Definition?.MaxLength !== 'undefined';
  const classes = [
    'FormFieldLabel',
    isReadOnly ? 'FormFieldLabel-readOnly' : '',
    isDisabled ? 'FormFieldLabel-disabled' : '',
  ];

  // Determine which background color to use
  const getBackgroundColor = () => {
    if (fieldPresentation.isFocused) {
      return 'warning.light';
    }
    if (fieldPresentation.isHighlighted) {
      return 'states.focus';
    }
    return 'transparent';
  };

  const isActive =
    fieldPresentation.isFocused || fieldPresentation.isHighlighted;

  return (
    <>
      <Box
        className={classes.join(' ')}
        sx={{
          display: 'flex',
          fontWeight: 'bold',
          mb: 1,
          color: isDisabled ? 'text.disabled' : 'text.primary',
          alignItems: 'center', // Align items vertically
        }}
      >
        <Box sx={{ position: 'relative', display: 'inline-flex' }}>
          {/* Background highlight bubble */}
          <Box
            sx={{
              position: 'absolute',
              top: '-4px',
              left: '-8px',
              right: '-8px',
              bottom: '-4px',
              backgroundColor: getBackgroundColor(),
              borderRadius: '8px',
              opacity: isActive ? 1 : 0,
              transition: `opacity .5s ease-in-out, background-color .5s ease-in-out`,
              zIndex: 0,
            }}
          />

          {/* Label text */}
          <Typography
            sx={{
              position: 'relative',
              zIndex: 1,
              textDecoration: hasHelpText ? 'underline' : '',
              textDecorationStyle: 'dashed',
              textDecorationColor: (theme) => theme.palette.states.focus,
              textUnderlineOffset: 6,
              fontWeight: 'bold',
              color: isDisabled ? 'text.disabled' : 'text.primary',
              '&:hover': { cursor: hasHelpText ? 'pointer' : 'default' },
            }}
            onClick={() => setIsHelpDrawerOpen(hasHelpText)}
          >
            {field.Label || fieldMeta?.Name}
          </Typography>

          {/* Required asterisk */}
          {isRequired && (
            <span
              style={{
                position: 'relative',
                zIndex: 1,
                marginLeft: '4px',
                lineHeight: '30px',
                height: '24px',
                color: 'red',
                fontSize: '28px',
                fontWeight: '0 !important',
              }}
            >
              *
            </span>
          )}
        </Box>

        {/* Badges (these stay outside the highlight) */}
        {renderRemainingLeft && (
          <Typography
            component="span"
            sx={{
              backgroundColor: 'grey.50',
              color: 'grey.500',
              padding: '2px 8px',
              borderRadius: '12px',
              fontSize: '13px',
              lineHeight: '18px',
              fontWeight: '500',
              marginLeft: '5px',
            }}
          >
            {Number(fieldMeta.Definition.MaxLength) - showRemaining} left
          </Typography>
        )}
        {fieldSourceValue && (
          <Typography
            component="span"
            sx={{
              backgroundColor: 'info.main',
              color: 'text.primary',
              padding: '2px 8px',
              borderRadius: '12px',
              fontSize: '13px',
              lineHeight: '18px',
              fontWeight: '500',
              marginLeft: '5px',
            }}
          >
            {fieldSourceValue}
          </Typography>
        )}
      </Box>
      <HelpTextSideDrawer
        isHelpDrawerOpen={isHelpDrawerOpen}
        setIsHelpDrawerOpen={setIsHelpDrawerOpen}
      />
    </>
  );
};
